import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { useAccount } from '@/hooks/useAccount'

// EP-160 T160-103l (PDPA s.23, AC-10): the operator's privacy notice, shown
// full screen after a lease-code login. It is a notice, not a consent, so
// there is only one way out: acknowledging it, which records that it was shown.
// It shows while the version the signed-in tenant last saw differs from the
// account payload's current one, and renders nothing otherwise (also nothing
// for an older bundled account snapshot that carries no privacy block).
export default function PrivacyNoticeSheet() {
  const { t } = useTranslation('auth')
  const { customer } = useAuth()
  const { account } = useAccount()
  const [acknowledgedVersion, setAcknowledgedVersion] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const privacy = account?.privacy
  if (
    !privacy ||
    !customer ||
    customer.notice_version === privacy.notice_version ||
    acknowledgedVersion === privacy.notice_version
  ) {
    return null
  }

  const acknowledge = async () => {
    setSaving(true)
    setFailed(false)
    try {
      await api.post('/api/customer/privacy_notice', { notice_version: privacy.notice_version })
      setAcknowledgedVersion(privacy.notice_version)
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-notice-title"
      className="fixed inset-0 z-50 bg-base-100 flex flex-col px-6 pt-safe-chrome pb-[calc(env(safe-area-inset-bottom)+1.5rem)] overflow-y-auto"
    >
      <div className="max-w-[430px] w-full mx-auto flex-1 flex flex-col justify-center gap-4">
        <h1 id="privacy-notice-title" className="text-sm font-semibold text-base-content/60">
          {t('privacy_notice_title')}
        </h1>
        <p className="text-xl font-bold text-base-content">
          {t('privacy_notice_controller', { name: privacy.controller_name })}
        </p>
        <p className="text-base-content/80 break-words">
          {t('privacy_notice_contact', { contact: privacy.controller_contact })}
        </p>
        <p className="text-base-content/80">
          {t('privacy_notice_processor', { processor: privacy.processor })}
        </p>
        <a
          href={privacy.policy_url}
          target="_blank"
          rel="noopener noreferrer"
          className="link link-primary"
        >
          {t('privacy_notice_policy_link')}
        </a>
        {failed && (
          <div role="alert" className="alert alert-error text-sm">
            <span>{t('privacy_notice_error')}</span>
          </div>
        )}
      </div>
      <div className="max-w-[430px] w-full mx-auto">
        <button
          className="btn btn-primary w-full"
          onClick={() => { void acknowledge() }}
          disabled={saving}
        >
          {saving ? <span className="loading loading-spinner loading-sm" /> : t('privacy_notice_acknowledge')}
        </button>
      </div>
    </div>
  )
}
